import { HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { environment } from 'src/environments/environment'

/**
 * Centralized HTTP error handler service
 *
 * Provides methods to:
 * 1. Extract real error messages from HTTP errors
 * 2. Determine if an error is being incorrectly reported as CORS
 * 3. Format user-friendly error messages
 */
@Injectable({
  providedIn: 'root',
})
export class HttpErrorHandlerService {
  /**
   * Handles HTTP operation that failed and lets the app continue.
   *
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  handleError<T>(operation = 'operation', result?: T) {
    return (error: HttpErrorResponse): Observable<T> => {
      // Log the detailed error info to console
      const errorDetails = this.getDiagnosticErrorMessage(error, operation)
      console.error(errorDetails.technicalMessage)

      // Check if this is likely a false CORS error
      this.diagnoseMisleadingCorsError(error)

      // Return a safe result so the application can continue
      return of(result as T)
    }
  }

  /**
   * Get a diagnostic error message for debugging
   */
  getDiagnosticErrorMessage(
    error: HttpErrorResponse,
    operation: string,
  ): {
    userMessage: string
    technicalMessage: string
  } {
    const timestamp = new Date().toISOString()

    // Default user-friendly message
    let userMessage = 'An error occurred. Please try again later.'

    // Default technical message (for logs/console)
    let technicalMessage = `[${timestamp}] Error in ${operation}: `

    if (error.status === 0) {
      // Connection error
      if (
        error.error instanceof ProgressEvent &&
        error.error.type === 'error'
      ) {
        technicalMessage += 'Network error - server unreachable'
        userMessage =
          'Unable to connect to the server. Please check your internet connection.'
      } else if (error.message && error.message.includes('CORS')) {
        technicalMessage += `Possible CORS misconfiguration: ${error.message}`
        if (!environment.production) {
          userMessage =
            'Cross-origin request blocked. This may be a server configuration issue.'
        }
      } else {
        technicalMessage += `Unknown client-side error: ${error.message}`
      }
    } else {
      // Server returned an error response
      technicalMessage += `Server error ${error.status}: ${error.statusText || 'Unknown'}, Body: ${JSON.stringify(error.error)}`

      // Try to extract a user-friendly message from the error
      if (error.error?.message) {
        // Check if this is a "Too Many Requests" error with status 500
        if (
          error.status === 500 &&
          (error.error.message.toLowerCase().includes('too many requests') ||
            error.error.message.toLowerCase().includes('rate exceeded'))
        ) {
          // Clean up the message for better user experience
          userMessage = 'The server is busy. Please try again in a moment.'
          technicalMessage += ` (Rate limit error disguised as 500, actual message: ${error.error.message})`
        } else {
          userMessage = error.error.message
        }
      } else if (error.error?.error) {
        userMessage = error.error.error
      } else {
        // Use status code to determine generic message
        userMessage = this.getMessageFromStatusCode(error.status)
      }
    }

    return { userMessage, technicalMessage }
  }

  /**
   * Diagnose if a CORS error might be misleading
   * Logs detailed warnings to help developers
   */
  public diagnoseMisleadingCorsError(error: HttpErrorResponse): boolean {
    if (error.message && error.message.includes('CORS')) {
      console.warn('⚠️ Potentially misleading CORS error detected!')
      console.warn('This may not be an actual CORS issue but could be:')
      console.warn('1. The server is not running or unreachable')
      console.warn('2. The API endpoint does not exist (404)')
      console.warn('3. The server is rejecting the request (401/403)')
      console.warn('4. A network timeout occurred')
      console.warn('5. The request was malformed (400)')

      console.warn('Debug steps:')
      console.warn('- Check the server logs')
      console.warn('- Try the request from a tool like Postman')
      console.warn('- Verify the endpoint URL is correct')
      console.warn('- Check authentication tokens')

      return true
    }
    return false
  }

  /**
   * Get a user-friendly message based on HTTP status code
   */
  private getMessageFromStatusCode(status: number): string {
    switch (status) {
      case 400:
        return 'The request was invalid. Please check your input.'
      case 401:
        return 'You need to log in to access this resource.'
      case 403:
        return 'You do not have permission to access this resource.'
      case 404:
        return 'The requested resource was not found.'
      case 408:
        return 'The request timed out. Please try again.'
      case 409:
        return 'There was a conflict with the current state of the resource.'
      case 422:
        return 'The server could not process your request.'
      case 429:
        return 'The server is busy. Please try again in a moment.'
      case 500:
        return 'The server encountered an internal error. Please try again later.'
      case 502:
      case 503:
      case 504:
        return 'The service is temporarily unavailable. Please try again later.'
      default:
        return `An error occurred (${status}). Please try again later.`
    }
  }
}
